import type { TextPart } from "ai";
import { Box, Editor, type BoxModel } from "tldraw";

import type {
  BlurryShapesPart,
  ContextItemsPart,
  TLDrawUIDataParts,
} from "../tldraw";
import { convertTldrawShapesToPeripheralShapes } from "../tldraw/format/convertTldrawShapesToPeripheralShapes";
import { convertTldrawShapeToBlurryShape } from "../tldraw/format/convertTldrawShapeToBlurryShape";
import { convertTldrawShapeToSimpleShape } from "../tldraw/format/convertTldrawShapeToSimpleShape";
import type { SimpleShape } from "../tldraw/format/SimpleShape";
import type { ContextItem } from "../tldraw/types/ContextItem";

const fieldConverters: {
  [K in keyof TLDrawUIDataParts]: (
    value: NonNullable<TLDrawUIDataParts[K]>,
  ) => string | undefined;
} = {
  blurryShapes: ({ shapes }: BlurryShapesPart) => {
    if (!shapes || shapes.length === 0)
      return "There are no shapes in your view at the moment.";

    return `These are the shapes you can currently see: ${JSON.stringify(shapes)}`;
  },
  contextItems: ({ items }: ContextItemsPart) => {
    const messages: string[] = [];

    const shapeItems = items.filter((item) => item.type === "shape");
    const shapesItems = items.filter((item) => item.type === "shapes");
    const areaItems = items.filter((item) => item.type === "area");
    const pointItems = items.filter((item) => item.type === "point");

    // Handle area context items
    if (areaItems.length > 0) {
      const areas = areaItems.map((item) => item.bounds);
      messages.push(
        'The user has specifically brought your attention to the following areas in this request. The user might refer to them as the "area(s)" or perhaps "here" or "there", but either way, it\'s implied that you should focus on these areas in both your reasoning and actions. Make sure to focus your task on these areas:',
      );
      for (const area of areas) {
        messages.push(JSON.stringify(area));
      }
    }

    // Handle point context items
    if (pointItems.length > 0) {
      const points = pointItems.map((item) => item.point);
      messages.push(
        'The user has specifically brought your attention to the following points in this request. The user might refer to them as the "point(s)" or perhaps "here" or "there", but either way, it\'s implied that you should focus on these points in both your reasoning and actions. Make sure to focus your task on these points:',
      );
      for (const point of points) {
        messages.push(JSON.stringify(point));
      }
    }

    // Handle individual shape context items
    if (shapeItems.length > 0) {
      const shapes = shapeItems.map((item) => item.shape);
      messages.push(
        `The user has specifically brought your attention to these ${shapes.length} shapes individually in this request. Make sure to focus your task on these shapes where applicable:`,
      );
      for (const shape of shapes) {
        messages.push(JSON.stringify(shape));
      }
    }

    // Handle groups of shapes context items
    for (const contextItem of shapesItems) {
      const shapes = contextItem.shapes;
      if (shapes.length > 0) {
        messages.push(
          `The user has specifically brought your attention to the following group of ${shapes.length} shapes in this request. Make sure to focus your task on these shapes where applicable:`,
        );
        messages.push(shapes.map((shape) => JSON.stringify(shape)).join("\n"));
      }
    }

    return messages.join("\n");
  },
  viewportBounds: ({ agentBounds, userBounds }) => {
    const agentViewportBounds = agentBounds;

    if (!agentViewportBounds || !userBounds) return undefined;

    const doUserAndAgentShareViewport =
      withinPercent(agentViewportBounds.x, userBounds.x, 5) &&
      withinPercent(agentViewportBounds.y, userBounds.y, 5) &&
      withinPercent(agentViewportBounds.w, userBounds.w, 5) &&
      withinPercent(agentViewportBounds.h, userBounds.h, 5);

    const agentViewportBoundsBox = Box.From(agentViewportBounds);
    const currentUserViewportBoundsBox = Box.From(userBounds);

    const agentContainsUser = agentViewportBoundsBox.contains(
      currentUserViewportBoundsBox,
    );
    const userContainsAgent = currentUserViewportBoundsBox.contains(
      agentViewportBoundsBox,
    );

    let relativeViewportDescription: string = "";

    if (doUserAndAgentShareViewport) {
      relativeViewportDescription = "is the same as";
    } else {
      if (agentContainsUser) {
        relativeViewportDescription = "contains";
      } else if (userContainsAgent) {
        relativeViewportDescription = "is contained by";
      } else {
        relativeViewportDescription = getRelativePositionDescription(
          agentViewportBounds,
          userBounds,
        );
      }
    }
    const response = [
      `The bounds of the part of the canvas that you can currently see are:`,
      JSON.stringify(agentBounds),
      `The user's view is ${relativeViewportDescription} your view.`,
    ];

    if (!doUserAndAgentShareViewport) {
      // If the user and agent share a viewport, we don't need to say anything about the bounds
      response.push(
        `The bounds of what the user can see are:`,
        JSON.stringify(userBounds),
      );
    }

    return response.join("\n");
  },
  peripheralShapes: ({ clusters }) => {
    if (!clusters || clusters.length === 0) return undefined;
    return `There are some groups of shapes in your peripheral vision, outside the your main view. You can't make out their details or content. If you want to see their content, you need to get closer. The groups are as follows: ${JSON.stringify(clusters)}`;
  },
  selectedShapes: ({ shapes }) => {
    if (!shapes || shapes.length === 0) return undefined;

    return `The user has selected these shapes. Focus your task on these shapes where applicable:
      ${shapes.map((shape) => JSON.stringify(shape)).join("\n")}`;
  },
};

function withinPercent(a: number, b: number, percent: number) {
  const max = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) <= (percent / 100) * max;
}

/**
 * Determines the relative position of box B from box A's perspective.
 */
function getRelativePositionDescription(
  boxA: BoxModel,
  boxB: BoxModel,
): string {
  // Find centers of both boxes
  const centerA = {
    x: boxA.x + boxA.w / 2,
    y: boxA.y + boxA.h / 2,
  };

  const centerB = {
    x: boxB.x + boxB.w / 2,
    y: boxB.y + boxB.h / 2,
  };

  // Calculate the difference vector from A to B
  const dx = centerB.x - centerA.x;
  const dy = centerB.y - centerA.y;

  // Handle the case where centers are the same
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
    return "is concentric with";
  }

  // Calculate angle from A to B (in radians)
  const angle = Math.atan2(dy, dx);

  // Convert to degrees and normalize to 0-360
  let degrees = (angle * 180) / Math.PI;
  if (degrees < 0) degrees += 360;

  // Map degrees to 8 cardinal/ordinal directions
  // 0° = right, 90° = bottom, 180° = left, 270° = top
  if (degrees >= 337.5 || degrees < 22.5) return "to the right of";
  if (degrees >= 22.5 && degrees < 67.5) return "to the bottom right of";
  if (degrees >= 67.5 && degrees < 112.5) return "below";
  if (degrees >= 112.5 && degrees < 157.5) return "to the bottom left of";
  if (degrees >= 157.5 && degrees < 202.5) return "to the left of";
  if (degrees >= 202.5 && degrees < 247.5) return "to the top left of";
  if (degrees >= 247.5 && degrees < 292.5) return "above";
  if (degrees >= 292.5 && degrees < 337.5) return "to the top right of";

  return "is different from";
}

function roundBox(boxModel: BoxModel): BoxModel {
  boxModel.x = Math.round(boxModel.x);
  boxModel.y = Math.round(boxModel.y);
  boxModel.w = Math.round(boxModel.w);
  boxModel.h = Math.round(boxModel.h);
  return boxModel;
}

export const convertFocusedSketch = (
  sketchName: string,
  parentPart: TLDrawUIDataParts,
) => {
  let text: string[] = [];
  const isConverterKey = (field: string): field is keyof TLDrawUIDataParts =>
    Object.prototype.hasOwnProperty.call(fieldConverters, field);
  const convertField = <K extends keyof TLDrawUIDataParts>(field: K) => {
    const value = parentPart[field];
    if (value === undefined) return;
    const converter = fieldConverters[field];
    if (!converter) return;
    const newMessage = converter(value);
    if (newMessage !== undefined) text.push(newMessage);
  };
  for (const field of Object.keys(parentPart)) {
    if (isConverterKey(field)) convertField(field);
  }
  if (text.length === 0) return undefined;
  return {
    type: "text",
    text: `
The user currently has the ${sketchName} sketch open. Here is a description of the sketch,
what is currently slected, where the user is currently looking, etc. There is also an screenshot
attached.
======= ${sketchName} Description ======
${text.join("\n")}
========================================`,
  } satisfies TextPart;
};

const blurryShapesPartBuilder = (editor: Editor, bounds: BoxModel) => {
  const shapes = editor.getCurrentPageShapesSorted();
  const contextBoundsBox = Box.From(bounds);

  // Get all shapes within the agent's viewport
  const shapesInBounds = shapes.filter((shape) => {
    if (!editor) return false;
    const bounds = editor.getShapeMaskedPageBounds(shape);
    if (!bounds) return false;
    return contextBoundsBox.includes(bounds);
  });

  // Convert the shapes to the blurry shape format
  const blurryShapes = shapesInBounds
    .map((shape) => {
      if (!editor) return null;
      return convertTldrawShapeToBlurryShape(editor, shape);
    })
    .filter((s) => s !== null);

  // Apply the offset and round the blurry shapes
  const normalizedBlurryShapes = blurryShapes.map((shape) => {
    const bounds = roundBox(shape);
    /*
    const bounds = roundBox(
      helpers.applyOffsetToBox({
        x: shape.x,
        y: shape.y,
        w: shape.w,
        h: shape.h,
      }),
    ); */
    return { ...shape, x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h };
  });

  return {
    shapes: normalizedBlurryShapes,
  };
};

const contextItemsPartBuilder = (contextItems: ContextItem[]) => {
  /*
  const items = contextItems.map((contextItem) => {
    const offsetContextItem = helpers.applyOffsetToContextItem(contextItem);
    return helpers.roundContextItem(offsetContextItem);
  });
  */

  return {
    items: contextItems,
  };
};

const viewportBoundsPartBuilder = (editor: Editor, bounds: BoxModel) => {
  const userBounds = editor.getViewportPageBounds();
  /*
  const offsetUserBounds = helpers.applyOffsetToBox(userBounds)
  const offsetAgentBounds = helpers.applyOffsetToBox(request.bounds)
  */

  return {
    userBounds: roundBox(userBounds),
    agentBounds: roundBox(bounds),
  };
};

const selectedShapesPartBuilder = (editor: Editor) => {
  const userSelectedShapes =
    editor.getSelectedShapes().map((v) => structuredClone(v)) ?? [];

  const simpleShapes: SimpleShape[] = [];
  for (const shape of userSelectedShapes) {
    if (!shape) continue;
    const simpleShape = convertTldrawShapeToSimpleShape(editor, shape);
    if (simpleShape) {
      simpleShapes.push(simpleShape);
    }
  }

  /*
    const normalizedSimpleShapes = simpleShapes.map((shape) => {
      const offsetShape = helpers.applyOffsetToShape(shape);
      return roundShape(offsetShape);
    });
    */

  return {
    shapes: simpleShapes,
  };
};

const peripheralShapesPartBuilder = (editor: Editor, bounds: BoxModel) => {
  const shapes = editor.getCurrentPageShapesSorted();
  const contextBoundsBox = Box.From(bounds);

  // Get all shapes that are outside the context bounds (these are what we want to peripheralize)
  const shapesOutsideViewport = shapes.filter((shape) => {
    if (!editor) return false;
    const bounds = editor.getShapeMaskedPageBounds(shape);
    if (!bounds) return;
    if (contextBoundsBox.includes(bounds)) return;
    return true;
  });

  // Convert the shapes to peripheral shape cluster format
  const clusters = convertTldrawShapesToPeripheralShapes(
    editor,
    shapesOutsideViewport,
    {
      padding: 75,
    },
  );

  // Apply the offset and round the clusters
  /*
		const normalizedClusters = clusters.map((cluster) => {
			const offsetBounds = helpers.applyOffsetToBox(cluster.bounds)
			return {
				numberOfShapes: cluster.numberOfShapes,
				bounds: helpers.roundBox(offsetBounds),
			}
		})
    */

  return {
    clusters,
  };
};

export const focusedSketchBuilder = ({
  editor,
  bounds,
  items,
}: {
  editor: Editor;
  bounds: BoxModel;
  items?: ContextItem[];
}) =>
  ({
    blurryShapes: blurryShapesPartBuilder(editor, bounds),
    peripheralShapes: peripheralShapesPartBuilder(editor, bounds),
    selectedShapes: selectedShapesPartBuilder(editor),
    viewportBounds: viewportBoundsPartBuilder(editor, bounds),
    contextItems: contextItemsPartBuilder(items ?? []),
  }) satisfies TLDrawUIDataParts;
